import React, { FC, useState, useEffect, Suspense } from 'react'
import { appConfig } from '../../../assets/customizations/config'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Tab from 'react-bootstrap/Tab'
import Nav from 'react-bootstrap/Nav'
import Button from 'react-bootstrap/Button'
import { useHistory, useParams } from 'react-router-dom'
import isAuthenticated from '../../../routes/PrivateRoute'

import Eobs from '../../../components/FhirData/FhirResources/Eobs'
import Immunizations from '../../../components/FhirData/FhirResources/Immunizations'
import MemberProfile from '../../../components/FhirData/FhirResources/MemberProfile'
import Observations from '../../../components/FhirData/FhirResources/Observations'
import Coverage from '../../../components/FhirData/FhirResources/Coverage'
import Conditions from '../../../components/FhirData/FhirResources/Conditions'
import Allergies from '../../../components/FhirData/FhirResources/Allergies'
import Medications from '../../../components/FhirData/FhirResources/Medications'
import Procedure from '../../../components/FhirData/FhirResources/Procedure'
import CarePlans from '../../../components/FhirData/FhirResources/CarePlans'
import CareTeams from '../../../components/FhirData/FhirResources/CareTeams'
import Encounters from '../../../components/FhirData/FhirResources/Encounters'
import DiagnosticReport from '../../../components/FhirData/FhirResources/DiagnosticReport'
import DigitalIdCard from '../../../components/FhirData/FhirResources/DigitalIdCard'
import Goals from '../../../components/FhirData/FhirResources/Goals'

import useUserWithRole from '../../../hooks/authentication/useUserWithRole'
import { useGlobalState } from '../../../hooks/store/useGlobalState'
import useFhirResource from '../../../hooks/admin/useFhirResource'
import usePaginatedFhirResource from '../../../hooks/admin/usePaginatedFhirResource'
import useEnvironment from '../../../hooks/location/useEnviroment'
import { isDemoTenant, scrollToTop } from '../../../utils/helpers'

import HumanName from '../../../components/FhirData/DataTypes/HumanName'
import IPS from '../../../components/FhirData/FhirResources/IPS'
import PageTitle from '../../../components/PageTitle'
import useAlert from '../../../hooks/authentication/useAlert'
import { Alert } from 'react-bootstrap'
import { useAuthState } from '../../../hooks/store/useAuthState'
import { CarePlanDisplayType } from '../../../types/CarePlanDisplayType'

type RouteParams = {
  page?: string;
  detailId?: string;
}

const getKeys = () => {
  const toolsKeys = [
    { label: 'Digital ID Card', resource: 'DigitalIdCard', show: true },
    { label: 'Patient Summary (IPS)', resource: 'IPS', show: true },
    { label: 'Data Sharing', resource: 'DataSharing', show: true },
  ]
  const memberKeys = [
    { label: 'Member Profile', resource: 'Patient', show: true },
    { label: 'Coverage', resource: 'Coverage', show: true },
    { label: 'EOBs', resource: 'ExplanationOfBenefit', show: true, longLabel: 'Explanation of Benefits (EOBs)' },
  ]
  const clinicalKeys = appConfig.clinical_fhir_data.filter((data) => data.show)
  const allKeys: {
    label: string;
    resource: string;
    show?: boolean;
    longLabel?: string
  }[] = [...toolsKeys, ...memberKeys, ...clinicalKeys]

  const keyResourceMap = allKeys.reduce((acc, { label, resource }) => {
    acc[label] = resource
    return acc
  }, {} as Record<string, string>)

  return { toolsKeys, memberKeys, clinicalKeys, allKeys, keyResourceMap }
}

const Member: FC = () => {
  const { data: environmentData } = useEnvironment()
  const fhirId = useGlobalState((state) => state.fhirId)

  const { toolsKeys, memberKeys, clinicalKeys, allKeys: keys } = getKeys()
  const { page, detailId } = useParams<RouteParams>()
  const initialKey = 'Patient'
  const [key, setKey] = useState(initialKey)
  const history = useHistory()

  //~ Dynamic outline for resource request
  const {
    data: resourceData,
    refetch: fetchResource,
    isStale,
    isFetching,
    handleChangePage,
    totalPages,
    currentPage,
  } = usePaginatedFhirResource({
    resource: key,
    memberId: fhirId,
  }, 25)

  const fhirResourcesConfig = [
    { key: 'patientData', resourceType: 'Patient', pageSize: 1 },
    { key: 'coverageData', resourceType: 'Coverage', pageSize: 25 },
  ]

  const fhirResources = fhirResourcesConfig.map(({ key, resourceType, pageSize }) => {
    const payload = {
      resource: resourceType,
      pageSize: pageSize,
      memberId: fhirId,
    }
    const { data, refetch, isStale, isFetching, isLoading } = useFhirResource(payload)
    return { key, data, refetch, isStale, isFetching, isLoading }
  })

  const {
    patientData,
    coverageData,
  } = fhirResources.reduce((acc, { key, data, refetch, isStale, isFetching, isLoading }) => {
    acc[key] = { data, refetch, isStale, isFetching, isLoading }
    return acc
  }, {} as Record<string, any>)

  const { role } = useUserWithRole()

  useEffect(() => {
    if (page) {
      const keyObj = keys.find((k) => k.resource === page)
      if (keyObj) {
        setKey(keyObj.resource)
      } else {
        history.replace('/404')
      }
    } else if (key !== 'Patient') {
      setKey('Patient')
    }
    scrollToTop()
  }, [page])

  const formatResourceName = (resource: string) =>
    resource.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')

  const keyObj = keys.find(k => k.resource === key)
  let displayName: string

  if (detailId) {
    displayName = formatResourceName(key)
  } else {
    displayName = keyObj ? keyObj.label : key
  }

  const resourceType = key

  useEffect(() => {
    if (detailId) {
      if (key === 'Coverage') {
        history.replace('/404')
      }

      scrollToTop()
    }
  }, [key, detailId])

  const handleChangeKey = (k: string | null) => {
    const key = k || ''

    if (key === 'DataSharing') {
      history.push('/data-sharing')
      return
    }

    if (key) {
      const path = key === 'Patient'
        ? '/patient'
        : `/patient/${key}`

      history.push(path)
    }

    scrollToTop()
  }

  useEffect(() => {
    if (!key) {
      return
    }

    if (key === 'Patient') {
      patientData.refetch()
      return
    }

    if (key === 'Coverage') {
      coverageData.refetch()
      return
    }

    fetchResource()
  }, [key])

  const renderFhirDataResource = (resource: string) => {

    switch (resource) {
      case 'DigitalIdCard':
        return (
          <DigitalIdCard
            coverageData={coverageData?.data}
            patientData={patientData?.data}
            isFetching={coverageData?.isLoading}
          />
        )
      case 'IPS':
        return (
          <IPS patientData={patientData.data} />
        )
      case 'Patient':
        return (
          <MemberProfile
            patientData={patientData?.data}
            loading={patientData?.isLoading || coverageData?.isLoading}
          />
        )
      case 'Coverage':
        return (
          <Coverage
            coverageData={coverageData?.data}
            loading={coverageData?.isLoading}
          />
        )
      case 'ExplanationOfBenefit':

        return (
          <Eobs
            eobData={resourceData}
            eobDetailId={detailId}
            page={page}
            isFetching={isFetching}
            totalPages={totalPages}
            currentPage={currentPage}
            handleChangePage={handleChangePage}
          />

        )

      case 'AllergyIntolerance':
        return (
          <Allergies
            allergyData={resourceData}
            isFetching={isFetching}
            allergyDetailId={detailId}
            page={page}
            totalPages={totalPages}
            currentPage={currentPage}
            handleChangePage={handleChangePage}
          />

        )
      case 'Condition':
        return (
          <Conditions
            conditionData={resourceData}
            isFetching={isFetching}
            conditionDetailId={detailId}
            page={page}
            totalPages={totalPages}
            currentPage={currentPage}
            handleChangePage={handleChangePage}
          />
        )
      case 'Immunization':
        return (
          <Immunizations
            immunizationData={resourceData}
            immunizationDetailId={detailId}
            page={page}
            isFetching={isFetching}
            totalPages={totalPages}
            currentPage={currentPage}
            handleChangePage={handleChangePage}
          />
        )
      case 'Observation':
        return (
          <Observations
            observationData={resourceData}
            observationDetailId={detailId}
            page={page}
            isFetching={isFetching}
            totalPages={totalPages}
            currentPage={currentPage}
            handleChangePage={handleChangePage}
          />

        )
      case 'MedicationRequest':
        return (
          <Medications
            patientData={patientData?.data}
            page={page}
            isFetching={isFetching}
            medicationData={resourceData}
            medicationDetailId={detailId}
            handleChangePage={handleChangePage}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        )
      case 'Procedure':
        return (
          <Procedure
            page={page}
            isFetching={isFetching}
            procedureData={resourceData}
            procedureDetailId={detailId}
            handleChangePage={handleChangePage}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        )
      case 'Encounter':
        return (
          <Encounters
            page={page}
            isFetching={isFetching}
            encounterData={resourceData}
            encounterDetailId={detailId}
            handleChangePage={handleChangePage}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        )
      case 'CarePlan':
        return (
          <CarePlans
            page={page}
            clientCarePlanType={environmentData?.clientCarePlanType ? environmentData.clientCarePlanType.toLocaleUpperCase() as CarePlanDisplayType : 'STANDARD'}
            isFetching={isFetching}
            carePlanData={resourceData}
            carePlanDetailId={detailId}
            handleChangePage={handleChangePage}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        )
      case 'CareTeam':
        return (
          <CareTeams
            page={page}
            isFetching={isFetching}
            careTeamData={resourceData}
            careTeamDetailId={detailId}
            handleChangePage={handleChangePage}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        )

      case 'DiagnosticReport':
        return (
          <DiagnosticReport
            page={page}
            isFetching={isFetching}
            diagnosticReportData={resourceData}
            diagnosticReportDetailId={detailId}
            handleChangePage={handleChangePage}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        )

      case 'Goal':
        return (
          <Goals
            page={page}
            isFetching={isFetching}
            goalData={resourceData}
            goalDetailId={detailId}
            handleChangePage={handleChangePage}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        )
      default:
        return <div>Not a supported Portal Resource</div>
    }
  }
  const {
    alert: loginAlert,
    handleCloseAlert: handleCloseLoginAlert,
    showAlert: showLoginAlert
  } = useAlert({ show: false, variant: 'success', message: '' })

  const { loginSuccess, setLoginSuccess } = useAuthState()

  useEffect(() => {
    if (loginSuccess) {
      showLoginAlert('success', 'Successfully logged in')
      setLoginSuccess(false)
    }
  }, [loginSuccess])

  return (
    <section className='fhirData'>
      <Container>
        <Tab.Container activeKey={key} onSelect={handleChangeKey} transition={false}>
          <PageTitle
            resourceType={resourceType}
            displayName={displayName}
          />
          <h1 className=' fw-bold mb-1 h2'>
            <HumanName data={patientData?.data?.name?.[0]} dataExtension={patientData?.data?._name} />
          </h1>
          <Row>
            <Col md={3}>
              <Nav variant='pills' className='flex-column'>
                {role === 'member' && isDemoTenant(environmentData?.tenant || '') && (
                  <>
                    <p className='text-uppercase fw-bold mt-3 mb-2 section-header'>Tools</p>
                    {toolsKeys.map((key) => (
                      <Nav.Item
                        className='patient-pill d-flex align-items-center gap-3'
                        key={`nav-item-${key.resource}`}
                      >
                        <Nav.Link eventKey={key.resource}>
                          {key.label}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </>
                )}
                <p className='text-uppercase fw-bold mt-3 mb-2 section-header'>Member Data</p>
                {
                  [...memberKeys, ...clinicalKeys].map((key) => (
                    <Nav.Item className='patient-pill' key={`nav-item-${key.resource}`}>
                      <Nav.Link eventKey={key.resource}>{key.label}</Nav.Link>
                    </Nav.Item>
                  ))
                }
              </Nav>
            </Col>
            <Col sm={9} className='mt-sm-0 mt-3'>
              {loginAlert.show && (
                <Alert variant={loginAlert.variant} onClose={handleCloseLoginAlert} dismissible>
                  {loginAlert.message}
                </Alert>
              )}
              <Tab.Content>
                <Tab.Pane
                  eventKey={key}
                >
                  <h2 className="fw-bold h3">{keyObj?.longLabel || keyObj?.label}</h2>
                  {!toolsKeys.some(tool => tool.resource === key) && (
                    <h3 className="h4">
                      <HumanName
                        data={patientData?.data?.name?.[0]}
                        dataExtension={patientData?.data?._name}
                      />
                    </h3>
                  )}
                  <Suspense
                    fallback={
                      <div
                        className="visually-hidden"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        Loading content…
                      </div>
                    }
                  >
                    {
                      key && renderFhirDataResource(key)
                    }
                  </Suspense>
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Container>
    </section >
  )
}

export default isAuthenticated(Member)